import { useState, useMemo, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "sonner";
import {
  useGetSecureVolumeProductsQuery,
  useGetSecureVolumeWorkQuery,
  useSaveSecureVolumeWorkMutation,
  useUploadSecureVolumeFileMutation,
  useMarkSecureVolumeSentMutation,
  useCompleteSecureVolumeMutation,
} from "../../../../store/services/secureVolumeService";
import {
  VolumeTypeEnum,
  WorkProgressStateEnum,
} from "../../../../store/types/secureVolume.types";
import type { QuarterVolume } from "../../../../store/types/secureVolume.types";
import { EventDataTypeEnum } from "../../../../store/types/work.types";
import { WorkTypeComponentProps } from "../WorkTypeComponentProps";
import { PROGRESS_STATE_LABELS } from "./types";
import {
  extractYears,
  computeGrandTotal,
  computeGrandQuarterTotal,
  getGrandSecuredPercentage,
  getGrandQuarterSecuredPercentage,
  getYearlyTypeMap,
  getSecuredPercentage,
  distributeTotalByHistory,
  getQuarterForecast,
  getQuarterVolumesByType,
  percentFromKwh,
} from "./helpers";
import { VolumeLegend } from "./components/VolumeLegend";
import { FacilityRow } from "./components/FacilityRow";
import { TotalEditCell } from "./components/TotalEditCell";
import { TotalDisplay } from "./components/TotalDisplay";
import { VolumeChart } from "./components/VolumeChart";
import { ForwardButton } from "./components/ForwardButton";
import { ContractFilesButton } from "./components/ContractFilesButton";
import { UPLOAD_CONFIGS, type UploadVariant } from "./components/uploadConfigs";
import { ContactRequirements } from "../../../contact-requirements/ContactRequirements";
import {
  findProduct,
  formatPeriod,
  formatProductPeriod,
  getManagementPeriodForTargetYear,
  getProductName,
  isManagementPeriodActive,
} from "../../../secure-volume/productLabels";

/** Fasta kolumnbredder i volymtabellen – övriga årskolumner delar resterande bredd lika */
const LABEL_COL_WIDTH = 76;
const INPUT_COL_WIDTH = 180;

const SecureVolumeDetails = ({ work }: WorkTypeComponentProps) => {
  const { data, isLoading, error } = useGetSecureVolumeWorkQuery({
    customerId: work.Customer.Id,
    workId: work.Id,
  });

  const [saveVolumes] = useSaveSecureVolumeWorkMutation();
  const [uploadFile] = useUploadSecureVolumeFileMutation();
  const [markSent] = useMarkSecureVolumeSentMutation();
  const [completeWork] = useCompleteSecureVolumeMutation();
  const [savingFacilityIndex, setSavingFacilityIndex] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [showChart, setShowChart] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [totalExpanded, setTotalExpanded] = useState(false);

  // Målår och produkt kommer från ärendet – räknas aldrig ut i portalen
  const inputYear = data?.SecureVolumes[0]?.TargetYear;
  const productNo = data?.SecureVolumes[0]?.ProductNo;
  const { data: products } = useGetSecureVolumeProductsQuery();
  const product = findProduct(products, productNo);
  const productName = getProductName(products, productNo);
  // Pågår förvaltningsperioden för ärendets målår? Då förvaltas volymerna och ska inte ändras.
  const managementActive = isManagementPeriodActive(product, inputYear);
  const managementPeriod =
    product && inputYear != null ? getManagementPeriodForTargetYear(product, inputYear) : null;

  const years = useMemo(() => {
    const minYear = new Date().getFullYear() - 1;
    const dataYears = extractYears(data?.SecureVolumes ?? []).filter((y) => y >= minYear);
    if (inputYear && !dataYears.includes(inputYear)) {
      dataYears.push(inputYear);
    }
    return dataYears;
  }, [data?.SecureVolumes, inputYear]);

  const handleInputChange = useCallback(
    (updates: Record<string, string>) => {
      setInputValues((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const handleFacilitySave = useCallback(
    async (facilityIndex: number, quarters: Record<number, number>) => {
      if (!data || !inputYear) return;
      const facility = data.SecureVolumes[facilityIndex];
      const keyPrefix = `f${facilityIndex}`;

      // Build flat QuarterVolumes array with Forecast + Secured/InProgress
      const quarterVolumes: QuarterVolume[] = [];

      for (const year of years) {
        for (let q = 1; q <= 4; q++) {
          // Forecast — from existing data
          const forecast = facility.QuarterVolumes.find(
            (v) =>
              v.Year === year &&
              v.Quarter === q &&
              v.VolumeType === VolumeTypeEnum.Forecast,
          );
          if (forecast) {
            quarterVolumes.push(forecast);
          }

          if (year === inputYear) {
            // InProgress — from user input for the editable year
            const qty = quarters[q] ?? 0;
            quarterVolumes.push({
              Year: year,
              Quarter: q,
              VolumeType: VolumeTypeEnum.InProgress,
              Quantity: qty,
            });
          } else {
            // Secured — from existing data for historical years
            const secured = facility.QuarterVolumes.find(
              (v) =>
                v.Year === year &&
                v.Quarter === q &&
                v.VolumeType === VolumeTypeEnum.Secured,
            );
            if (secured) {
              quarterVolumes.push(secured);
            }
          }
        }
      }

      setSavingFacilityIndex(facilityIndex);
      try {
        // onQueryStarted i mutationen uppdaterar cachen optimistiskt
        await saveVolumes({
          customerNo: work.Customer.Id,
          workId: work.Id,
          body: {
            BillingPointId: facility.CustomerBillingPointNo,
            QuarterVolumes: quarterVolumes,
          },
        }).unwrap();

        // Rensa inputValues – cachen har redan rätt data
        setInputValues((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(next)) {
            if (key.startsWith(`${keyPrefix}-`)) {
              delete next[key];
            }
          }
          return next;
        });
      } catch {
        // onQueryStarted gör undo + toast automatiskt
      } finally {
        setSavingFacilityIndex(null);
      }
    },
    [data, inputYear, years, saveVolumes, work.Customer.Id, work.Id],
  );

  // Grand total distribution: spread across all facilities by historical secured pattern
  const handleGrandDistribute = useCallback(
    (total: number) => {
      if (!data || !inputYear) return;

      const allVolumes = data.SecureVolumes.flatMap((f) => f.QuarterVolumes);
      const historicYears = [...new Set(allVolumes.map((v) => v.Year))]
        .filter((y) => y < inputYear)
        .sort((a, b) => b - a);

      // Hitta närmaste år med säkrad data per anläggning
      let facilityWeights: number[] | null = null;
      for (const prevYear of historicYears) {
        const perFacility = data.SecureVolumes.map((f) =>
          f.QuarterVolumes
            .filter((v) => v.Year === prevYear && v.VolumeType === VolumeTypeEnum.Secured)
            .reduce((s, v) => s + v.Quantity, 0),
        );
        if (perFacility.reduce((s, v) => s + v, 0) > 0) {
          facilityWeights = perFacility;
          break;
        }
      }

      // Fallback: forecast
      if (!facilityWeights) {
        const perFacility = data.SecureVolumes.map((f) =>
          f.QuarterVolumes
            .filter((v) => v.Year === inputYear && v.VolumeType === VolumeTypeEnum.Forecast)
            .reduce((s, v) => s + v.Quantity, 0),
        );
        if (perFacility.reduce((s, v) => s + v, 0) > 0) {
          facilityWeights = perFacility;
        }
      }

      const updates: Record<string, string> = {};

      const applyDistribution = (facility: typeof data.SecureVolumes[0], i: number, share: number) => {
        const distributed = distributeTotalByHistory(
          share,
          facility.QuarterVolumes,
          inputYear,
        );
        for (let q = 1; q <= 4; q++) {
          const kwh = distributed[q];
          const forecast = getQuarterForecast(facility.QuarterVolumes, inputYear, q);
          updates[`f${i}-${inputYear}-${q}`] = kwh.toString();
          updates[`f${i}-${inputYear}-${q}-pct`] = forecast > 0
            ? percentFromKwh(kwh, forecast).toFixed(1)
            : "";
        }
      };

      if (!facilityWeights) {
        const perFacility = Math.round(total / data.SecureVolumes.length);
        data.SecureVolumes.forEach((facility, i) => {
          applyDistribution(facility, i, perFacility);
        });
      } else {
        const weightTotal = facilityWeights.reduce((s, v) => s + v, 0);
        data.SecureVolumes.forEach((facility, i) => {
          const facilityShare = Math.round(
            (total * facilityWeights[i]) / weightTotal,
          );
          applyDistribution(facility, i, facilityShare);
        });
      }

      handleInputChange(updates);
    },
    [data, inputYear, handleInputChange],
  );

  // Quarter-level percentage: each facility gets percent% of its own forecast for that quarter
  const handleGrandQuarterDistributeByPercent = useCallback(
    (percent: number, quarter: number) => {
      if (!data || !inputYear) return;

      const updates: Record<string, string> = {};
      const step = 100;

      data.SecureVolumes.forEach((facility, i) => {
        const isEditable = !facility.QuarterVolumes.some(
          (v) => v.Year === inputYear && v.VolumeType === VolumeTypeEnum.Secured && v.Quantity > 0,
        );
        if (!isEditable) return;

        const forecast = getQuarterForecast(facility.QuarterVolumes, inputYear, quarter);
        const kwh = Math.round((forecast * percent) / 100 / step) * step;
        updates[`f${i}-${inputYear}-${quarter}`] = kwh.toString();
        updates[`f${i}-${inputYear}-${quarter}-pct`] = forecast > 0
          ? percentFromKwh(kwh, forecast).toFixed(1)
          : "";
      });

      handleInputChange(updates);
    },
    [data, inputYear, handleInputChange],
  );

  // Quarter-level distribution: spread a single quarter's total across all editable facilities
  // Uses forecast proportion directly (not historical pattern) since quarter-level edits
  // are typically used to override history.
  const handleGrandQuarterDistribute = useCallback(
    (quarterTotal: number, quarter: number) => {
      if (!data || !inputYear) return;

      // Only distribute to editable facilities (not already Secured for inputYear)
      const editableFacilities = data.SecureVolumes
        .map((f, i) => ({ facility: f, index: i }))
        .filter(({ facility }) =>
          !facility.QuarterVolumes.some(
            (v) => v.Year === inputYear && v.VolumeType === VolumeTypeEnum.Secured && v.Quantity > 0,
          ),
        );

      if (editableFacilities.length === 0) return;

      // Weights based on each facility's forecast for this quarter
      const facilityForecasts = editableFacilities.map(({ facility }) =>
        getQuarterForecast(facility.QuarterVolumes, inputYear, quarter),
      );
      const forecastTotal = facilityForecasts.reduce((s, v) => s + v, 0);

      const updates: Record<string, string> = {};
      const step = 100;

      if (forecastTotal === 0) {
        // No forecast — equal distribution
        const perFacility = Math.round(quarterTotal / editableFacilities.length / step) * step;
        editableFacilities.forEach(({ facility, index }, arrIdx) => {
          const kwh = arrIdx === 0
            ? perFacility + Math.round((quarterTotal - perFacility * editableFacilities.length) / step) * step
            : perFacility;
          const forecast = getQuarterForecast(facility.QuarterVolumes, inputYear, quarter);
          updates[`f${index}-${inputYear}-${quarter}`] = kwh.toString();
          updates[`f${index}-${inputYear}-${quarter}-pct`] = forecast > 0
            ? percentFromKwh(kwh, forecast).toFixed(1)
            : "";
        });
      } else {
        const distributed = editableFacilities.map((_, i) =>
          Math.round((quarterTotal * facilityForecasts[i]) / forecastTotal / step) * step,
        );
        // Remainder correction
        const sum = distributed.reduce((s, v) => s + v, 0);
        const diff = Math.round((quarterTotal - sum) / step) * step;
        if (diff !== 0) {
          const maxIdx = facilityForecasts.indexOf(Math.max(...facilityForecasts));
          distributed[maxIdx] += diff;
        }

        editableFacilities.forEach(({ facility, index }, arrIdx) => {
          const kwh = distributed[arrIdx];
          const forecast = getQuarterForecast(facility.QuarterVolumes, inputYear, quarter);
          updates[`f${index}-${inputYear}-${quarter}`] = kwh.toString();
          updates[`f${index}-${inputYear}-${quarter}-pct`] = forecast > 0
            ? percentFromKwh(kwh, forecast).toFixed(1)
            : "";
        });
      }

      handleInputChange(updates);
    },
    [data, inputYear, handleInputChange],
  );

  // Compute grand totals for historical years (memoized)
  const grandTotalTypeMaps = useMemo(() => {
    if (!data) return new Map<number, Map<VolumeTypeEnum, number>>();
    const result = new Map<number, Map<VolumeTypeEnum, number>>();
    for (const year of years) {
      const typeMap = new Map<VolumeTypeEnum, number>();
      for (const f of data.SecureVolumes) {
        const fMap = getYearlyTypeMap(f.QuarterVolumes, year);
        for (const [k, v] of fMap) {
          typeMap.set(k, (typeMap.get(k) ?? 0) + v);
        }
      }
      result.set(year, typeMap);
    }
    return result;
  }, [data, years]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary">
            Laddar volymdata...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        Det gick inte att ladda volymdata. Försök igen senare.
      </Alert>
    );
  }

  if (!data) return null;

  const progressState =
    PROGRESS_STATE_LABELS[data.WorkProgressState] ??
    PROGRESS_STATE_LABELS[WorkProgressStateEnum.NotSet];

  const eventDatas = work.EventDatas || [];
  const hasContract = eventDatas.some(
    (e) => e.Type === EventDataTypeEnum.SecureVolumeContractFile,
  );
  const hasSigned = eventDatas.some(
    (e) => e.Type === EventDataTypeEnum.SecureVolumeSignedContractFile,
  );

  const allVolumes = data.SecureVolumes.flatMap((f) => f.QuarterVolumes);

  const isAnyEditable = inputYear != null && data.SecureVolumes.some(
    (f) => !f.QuarterVolumes.some(
      (v) => v.Year === inputYear && v.VolumeType === VolumeTypeEnum.Secured && v.Quantity > 0,
    ),
  );

  const canDrop = !hasContract || !hasSigned;

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canDrop) return;
    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (hasContract && !hasSigned) {
      // Avtal finns, saknar signerat — ladda upp direkt som signerat
      uploadFile({
        customerId: work.Customer.Id,
        workId: work.Id,
        file,
        eventDataType: EventDataTypeEnum.SecureVolumeSignedContractFile,
      })
        .unwrap()
        .then(() => toast.success("Signerat avtal uppladdad!"))
        .catch(() => toast.error("Det gick inte att ladda upp filen."));
    } else {
      // Inget avtal — visa val-dialog
      setDroppedFile(file);
    }
  };

  const handleUploadVariant = async (variant: UploadVariant) => {
    if (!droppedFile) return;
    const config = UPLOAD_CONFIGS[variant];
    setDroppedFile(null);
    try {
      await uploadFile({
        customerId: work.Customer.Id,
        workId: work.Id,
        file: droppedFile,
        eventDataType: config.eventDataType,
      }).unwrap();
      toast.success("Fil uppladdad!");
    } catch {
      toast.error("Det gick inte att ladda upp filen.");
    }
  };

  return (
    <Stack
      spacing={2}
      onDragOver={(e) => {
        e.preventDefault();
        if (canDrop) setIsDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIsDragging(false);
      }}
      onDrop={handleDrop}
      sx={{ position: "relative", margin: -2 }}
    >
      {/* Drop overlay */}
      {isDragging && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            bgcolor: "rgba(25, 118, 210, 0.08)",
            border: "2px dashed",
            borderColor: "primary.main",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Stack alignItems="center" spacing={1}>
            <UploadFileIcon sx={{ fontSize: 48, color: "primary.main" }} />
            <Typography variant="subtitle1" color="primary">
              {hasContract ? "Släpp fil för att ladda upp signerat avtal" : "Släpp fil för att ladda upp avtal"}
            </Typography>
          </Stack>
        </Box>
      )}

      <ContactRequirements customerNo={work.Customer.Id} />
      {managementActive && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <strong>Förvaltningsperioden pågår</strong>
          {managementPeriod ? ` (${formatPeriod(managementPeriod)})` : ""}. Volymerna för{" "}
          {inputYear} förvaltas nu – ändra inget i ärendet utan att först stämma av med förvaltningen.
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "nowrap",
        }}
      >
        {/* Vänster: status + produkt/målår. Får krympa (ellips) så att knapparna till höger aldrig radbryts */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Chip
            label={progressState.label}
            color={progressState.color}
            size="small"
            sx={{ flexShrink: 0 }}
          />
          {(productName || inputYear != null) && (
            <Tooltip
              title={[
                productName,
                inputYear != null ? `Handlar för ${inputYear}` : null,
                product ? formatProductPeriod(product) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              <Chip
                label={[productName, inputYear].filter((x) => x != null).join(" · ")}
                size="small"
                variant="outlined"
                color={managementActive ? "warning" : "primary"}
                sx={{ minWidth: 0, maxWidth: "100%" }}
              />
            </Tooltip>
          )}
        </Stack>
        {/* Höger: filer, graf, åtgärdsknappar – radbryts aldrig */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <ContractFilesButton
            eventDatas={eventDatas}
            customerId={work.Customer.Id}
            workId={work.Id}
            hasContract={hasContract}
            hasSigned={hasSigned}
          />
          <Tooltip title={showChart ? "Dölj graf" : "Visa graf"}>
            <IconButton
              size="small"
              onClick={() => setShowChart((v) => !v)}
              color={showChart ? "primary" : "default"}
            >
              <BarChartIcon />
            </IconButton>
          </Tooltip>
          {!hasContract && !hasSigned && (
            <ForwardButton customerId={work.Customer.Id} workId={work.Id} />
          )}
          {data.WorkProgressState === WorkProgressStateEnum.Submitted && (
            <ForwardButton customerId={work.Customer.Id} workId={work.Id} fromZynergy />
          )}
          {hasContract && !hasSigned && data.WorkProgressState < WorkProgressStateEnum.ContractSent && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SendIcon />}
              onClick={async () => {
                try {
                  await markSent({ customerId: work.Customer.Id, workId: work.Id }).unwrap();
                  toast.success("Markerat som skickat!");
                } catch {
                  toast.error("Det gick inte att uppdatera status.");
                }
              }}
            >
              Markera skickat
            </Button>
          )}
          {(hasContract || hasSigned) && data.WorkProgressState < WorkProgressStateEnum.Completed && (
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={async () => {
                try {
                  await completeWork({ customerId: work.Customer.Id, workId: work.Id }).unwrap();
                  toast.success("Ärende klarmarkerat!");
                } catch {
                  toast.error("Det gick inte att klarmarkera ärendet.");
                }
              }}
            >
              Klarmarkera
            </Button>
          )}
        </Stack>
      </Box>

      {/* Chart */}
      <Collapse in={showChart} unmountOnExit>
        <VolumeChart
          facilities={data.SecureVolumes}
          years={years}
          inputYear={inputYear}
          inputValues={inputValues}
        />
      </Collapse>

      {/* Legend */}
      <VolumeLegend />

      {/* Facility grid */}
      {data.SecureVolumes.length > 0 ? (
        <TableContainer>
          <Table
            size="small"
            sx={{
              // Fast layout: label-kolumn + lika breda årskolumner, så att alla rader
              // (totalt, anläggning, kvartal) ligger exakt i linje oavsett innehåll
              tableLayout: "fixed",
              width: "100%",
              minWidth: LABEL_COL_WIDTH + INPUT_COL_WIDTH + (years.length - 1) * 100,
              "& .MuiTableCell-root": { px: 1 },
            }}
          >
            <colgroup>
              <col style={{ width: LABEL_COL_WIDTH }} />
              {years.map((year) => (
                // Målåret har inputfält + växlare och behöver mer plats än historikkolumnerna
                <col
                  key={year}
                  style={isAnyEditable && year === inputYear ? { width: INPUT_COL_WIDTH } : undefined}
                />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell />
                {years.map((year) => (
                  <TableCell
                    key={year}
                    align="right"
                    sx={{
                      borderLeft: "1px solid",
                      borderLeftColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2">{year}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Grand total row */}
              <TableRow
                onClick={() => setTotalExpanded((v) => !v)}
                sx={{
                  bgcolor: "action.hover",
                  cursor: "pointer",
                  "& > *": { borderBottom: totalExpanded ? undefined : 2, borderColor: "divider" },
                }}
              >
                <TableCell sx={{ py: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton size="small" sx={{ p: 0 }}>
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          transform: totalExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      />
                    </IconButton>
                    <Typography variant="subtitle2">Totalt</Typography>
                  </Box>
                </TableCell>
                {years.map((year) => {
                  if (isAnyEditable && year === inputYear) {
                    const grandTotal = computeGrandTotal(
                      data.SecureVolumes,
                      year,
                      inputValues,
                    );
                    const grandPct = getGrandSecuredPercentage(
                      data.SecureVolumes,
                      year,
                      inputValues,
                    );
                    return (
                      <TableCell
                        key={year}
                        align="right"
                        sx={{
                          py: 0.75,
                          borderLeft: "1px solid",
                          borderLeftColor: "divider",
                        }}
                      >
                        <TotalEditCell
                          currentTotal={grandTotal}
                          securedPct={grandPct}
                          onDistribute={handleGrandDistribute}
                        />
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      key={year}
                      align="right"
                      sx={{
                        py: 0.75,
                        borderLeft: "1px solid",
                        borderLeftColor: "divider",
                      }}
                    >
                      <TotalDisplay
                        typeMap={
                          grandTotalTypeMaps.get(year) ??
                          new Map<VolumeTypeEnum, number>()
                        }
                        securedPct={getSecuredPercentage(allVolumes, year)}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Quarter breakdown rows */}
              {totalExpanded &&
                [1, 2, 3, 4].map((q) => (
                  <TableRow
                    key={`q${q}`}
                    sx={{
                      bgcolor: "action.hover",
                      "& > *": {
                        borderBottom: q === 4 ? 2 : undefined,
                        borderColor: "divider",
                      },
                    }}
                  >
                    <TableCell sx={{ py: 0.5, pl: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Q{q}
                      </Typography>
                    </TableCell>
                    {years.map((year) => {
                      if (isAnyEditable && year === inputYear) {
                        const quarterTotal = computeGrandQuarterTotal(
                          data.SecureVolumes,
                          year,
                          q,
                          inputValues,
                        );
                        const quarterPct = getGrandQuarterSecuredPercentage(
                          data.SecureVolumes,
                          year,
                          q,
                          inputValues,
                        );
                        return (
                          <TableCell
                            key={year}
                            align="right"
                            sx={{
                              py: 0.5,
                              borderLeft: "1px solid",
                              borderLeftColor: "divider",
                            }}
                          >
                            <TotalEditCell
                              currentTotal={quarterTotal}
                              securedPct={quarterPct}
                              onDistribute={(total) => handleGrandQuarterDistribute(total, q)}
                              onDistributePercent={(pct) => handleGrandQuarterDistributeByPercent(pct, q)}
                              label={`Q${q}`}
                            />
                          </TableCell>
                        );
                      }
                      const qTypeMap = new Map<VolumeTypeEnum, number>();
                      for (const f of data.SecureVolumes) {
                        const fMap = getQuarterVolumesByType(f.QuarterVolumes, year, q);
                        for (const [k, v] of fMap) {
                          qTypeMap.set(k, (qTypeMap.get(k) ?? 0) + v);
                        }
                      }
                      // Visa bara en av Secured/InProgress — Secured har företräde
                      if ((qTypeMap.get(VolumeTypeEnum.Secured) ?? 0) > 0) {
                        qTypeMap.delete(VolumeTypeEnum.InProgress);
                      } else if (qTypeMap.has(VolumeTypeEnum.InProgress)) {
                        qTypeMap.delete(VolumeTypeEnum.Secured);
                      } else {
                        // Ingen av dem finns — visa Säkrad 0
                        qTypeMap.set(VolumeTypeEnum.Secured, 0);
                      }
                      const forecast = qTypeMap.get(VolumeTypeEnum.Forecast) ?? 0;
                      const secured = (qTypeMap.get(VolumeTypeEnum.Secured) ?? 0) +
                        (qTypeMap.get(VolumeTypeEnum.InProgress) ?? 0);
                      const qPct = forecast > 0 ? (secured / forecast) * 100 : null;

                      return (
                        <TableCell
                          key={year}
                          align="right"
                          sx={{
                            py: 0.5,
                            borderLeft: "1px solid",
                            borderLeftColor: "divider",
                          }}
                        >
                          <TotalDisplay typeMap={qTypeMap} securedPct={qPct} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

              {/* Facility rows */}
              {data.SecureVolumes.map((facility, index) => (
                <FacilityRow
                  key={index}
                  facility={facility}
                  index={index}
                  years={years}
                  inputYear={inputYear!}
                  inputValues={inputValues}
                  onInputChange={handleInputChange}
                  onSave={handleFacilitySave}
                  isSaving={savingFacilityIndex === index}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Inga anläggningar tillgängliga.
        </Typography>
      )}

      {/* Upload type picker dialog */}
      <Dialog open={!!droppedFile} onClose={() => setDroppedFile(null)}>
        <DialogTitle>Välj dokumenttyp</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {droppedFile?.name}
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => handleUploadVariant("contract")}
              fullWidth
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              Avtal
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<VerifiedUserIcon />}
              onClick={() => handleUploadVariant("signed")}
              fullWidth
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              Signerat avtal
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDroppedFile(null)}>Avbryt</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SecureVolumeDetails;
